import { useEffect } from "react";

type PageMeta = {
  /** Nur der Seiten-spezifische Teil (ohne " — brings-mit.com"). */
  title: string;
  /** Meta-Description (max ~160 Zeichen Empfehlung). */
  description?: string;
  /** Canonical-URL — absolut. Wenn nicht gesetzt, nimmt Hook window.location. */
  canonical?: string;
  /** OG-Image-URL — absolut. Wenn nicht gesetzt, wird das aus index.html beibehalten. */
  ogImage?: string;
  /** Meta-Robots — z. B. "noindex,nofollow" für Admin-/User-Seiten mit Keys in der URL. */
  robots?: string;
};

const SUFFIX = " — brings-mit.com";

function ensureMeta(selector: string, attr: string, attrValue: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  return el;
}

function setMeta(nameOrProp: "name" | "property", value: string, content: string) {
  const sel = `meta[${nameOrProp}="${value}"]`;
  const el = ensureMeta(sel, nameOrProp, value);
  el.setAttribute("content", content);
}

function setCanonical(href: string) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.href = href;
}

/**
 * Setzt document.title, Meta-Description, OpenGraph- und Twitter-Tags sowie
 * die kanonische URL. Läuft client-seitig, moderne Crawler (Google, Bing)
 * werten das aus. Kein SSR nötig.
 */
export function usePageMeta(meta: PageMeta) {
  const { title, description, canonical, ogImage, robots } = meta;

  useEffect(() => {
    const fullTitle = title ? `${title}${SUFFIX}` : `brings-mit.com — Wer bringt was mit?`;
    document.title = fullTitle;

    if (description) {
      setMeta("name", "description", description);
      setMeta("property", "og:description", description);
      setMeta("name", "twitter:description", description);
    }

    setMeta("property", "og:title", fullTitle);
    setMeta("name", "twitter:title", fullTitle);

    const href =
      canonical ??
      (typeof window !== "undefined"
        ? window.location.origin + window.location.pathname
        : "");
    if (href) {
      setCanonical(href);
      setMeta("property", "og:url", href);
    }

    if (ogImage) {
      setMeta("property", "og:image", ogImage);
      setMeta("name", "twitter:image", ogImage);
    }

    if (robots) {
      setMeta("name", "robots", robots);
    }
  }, [title, description, canonical, ogImage, robots]);
}
